import {
  FieldChoice,
  FieldType,
  IndexedParticipation,
  ParticipantFieldType,
  Recruitment,
  Session,
  SessionQuota,
  StudyType,
  useForm,
  useHaveQuota,
  useParticipantFieldsByName,
  useRecruitment,
  TabStatus,
} from '@app/api';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionsCell,
  ActionsHeader,
  ActionsLoader,
  DeviceCell,
  DeviceHeader,
  DurationCell,
  MeetingDateCell,
  NameCell,
  NameHeader,
  ParticipationDefaultCell,
  ScreenerCell,
  ScreenerHeader,
  ScreenerLoader,
  SourceCell,
  SourceHeader,
  SourceLoader,
  StatusCell,
  StatusHeader,
  StatusLoader,
  TagsCell,
  TagsHeader,
  TagsLoader,
} from '../components/ParticipationsTable';
import { getQuestionNumber } from 'components/CustomForm/FormBuilder/lib/getQuestionNumber';
import { ActionsConfig } from './useParticipationTableActions';

export interface FieldConfig {
  field: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  type?: FieldType | ParticipantFieldType;
  relevant: boolean;
  renderHeader?: (config: FieldConfig) => ReactNode;
  renderCell?: (
    participation: IndexedParticipation,
    config: FieldConfig,
  ) => ReactNode;
  renderLoadingCell?: (config: FieldConfig) => ReactNode;
}

const useCriteriaFieldConfigs = (recruitment?: Recruitment): FieldConfig[] => {
  const participantFieldsByName = useParticipantFieldsByName();
  return useMemo(() => {
    if (!recruitment?.criteria) {
      return [];
    }

    return recruitment.criteria
      .filter(({ fieldRef }) => participantFieldsByName[fieldRef])
      .map(({ fieldRef }) => {
        const field = participantFieldsByName[fieldRef];
        const values = field?.choices || [];

        return {
          field: field.name,
          label: field.range?.label || field.label || '',
          sortable: true,
          relevant: true,
          type: field.type,
          renderHeader: (config) => (
            <ScreenerHeader
              config={config}
              values={values}
              loadOnlyUsedValues={values.length > 10}
            />
          ),
          renderCell: (participation, config) => (
            <ScreenerCell participation={participation} config={config} />
          ),
          renderLoadingCell: (config) => <ScreenerLoader config={config} />,
        };
      });
  }, [recruitment, participantFieldsByName]);
};

const useFormFieldConfigs = (recruitment?: Recruitment): FieldConfig[] => {
  const { data: form } = useForm(recruitment?.screeningFormId);
  return useMemo(() => {
    if (!form?.fields) {
      return [];
    }

    return form.fields.map((f) => {
      let values: FieldChoice[] = [];
      if (
        f.type === FieldType.SINGLE_CHOICE ||
        f.type === FieldType.MULTIPLE_CHOICE
      ) {
        values = f.properties?.choices || [];
      }
      return {
        field: f.ref,
        label: `Q${getQuestionNumber(form.fields ?? [], f)} - ${f.title}`,
        sortable: true,
        relevant: true,
        type: f.type,
        renderHeader: (config) => (
          <ScreenerHeader config={config} values={values} loadOldValues />
        ),
        renderCell: (participation, config) => (
          <ScreenerCell participation={participation} config={config} />
        ),
        renderLoadingCell: (config) => <ScreenerLoader config={config} />,
      };
    });
  }, [form]);
};

export const useFieldConfig = (
  session: Session,
  tabKey: TabStatus,
  actionsConfig: ActionsConfig,
): FieldConfig[] => {
  const { t } = useTranslation();
  const haveDeviceQuota = useHaveQuota(
    session.id,
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  );
  const isModerated = session.studyType === StudyType.MODERATED;
  const hasTags = !!session.availableTags?.length;
  const { data: recruitment } = useRecruitment(session.recruitmentId);

  const criteriaFields = useCriteriaFieldConfigs(recruitment);
  const formFields = useFormFieldConfigs(recruitment);

  return useMemo(
    () => [
      {
        field: 'appliedAt',
        relevant: true,
        label: t('sessions.details.participantsDetails.columns.appliedAt'),
        sortable: true,
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'status',
        label: t('sessions.details.participantsDetails.columns.status'),
        filterable: true,
        relevant: true,
        type: ParticipantFieldType.SHORT_TEXT,
        renderHeader: () => <StatusHeader />,
        renderCell: (participation) => (
          <StatusCell participation={participation} />
        ),
        renderLoadingCell: () => <StatusLoader />,
      },
      {
        field: 'source',
        label: t('sessions.details.participantsDetails.columns.source'),
        filterable: true,
        relevant: true,
        type: ParticipantFieldType.SHORT_TEXT,
        renderHeader: () => <SourceHeader />,
        renderCell: (participation) => (
          <SourceCell participation={participation} />
        ),
        renderLoadingCell: () => <SourceLoader />,
      },
      {
        field: 'trackingId',
        label: t('sessions.details.participantsDetails.columns.tid'),
        filterable: true,
        relevant: true,
        type: FieldType.SHORT_TEXT,
        renderCell: (participation) => (
          <ParticipationDefaultCell
            field="trackingId"
            participation={participation}
            important
          />
        ),
      },
      {
        field: 'name',
        label: t('sessions.details.participantsDetails.columns.name'),
        sortable: true,
        filterable: true,
        relevant: true,
        type: FieldType.SHORT_TEXT,
        renderHeader: () => <NameHeader />,
        renderCell: (participation) => (
          <NameCell participation={participation} />
        ),
      },
      {
        field: 'tags',
        label: t('sessions.details.participantsDetails.columns.tags'),
        filterable: true,
        relevant: hasTags,
        type: ParticipantFieldType.TAGS,
        renderHeader: () => <TagsHeader />,
        renderCell: (participation) => (
          <TagsCell participation={participation} />
        ),
        renderLoadingCell: () => <TagsLoader />,
      },
      {
        field: 'meetingDate',
        label: t('sessions.details.participantsDetails.columns.meetingDate'),
        sortable: true,
        relevant: isModerated && tabKey === TabStatus.selected,
        type: ParticipantFieldType.DATE,
        renderCell: (participation) => (
          <MeetingDateCell participation={participation} />
        ),
      },
      {
        field: 'meetingVideoCallLink',
        label: t('sessions.details.participantsDetails.columns.meetingLink'),
        sortable: false,
        relevant: isModerated && tabKey === TabStatus.selected,
        type: ParticipantFieldType.SHORT_TEXT,
      },
      {
        field: 'usedDevice',
        label: t('sessions.details.participantsDetails.columns.device'),
        sortable: false,
        relevant: isModerated
          ? [TabStatus.applied, TabStatus.selected].includes(tabKey) &&
            haveDeviceQuota
          : tabKey === TabStatus.participated,
        type: ParticipantFieldType.SHORT_TEXT,
        renderHeader: () => <DeviceHeader />,
        renderCell: (participation) => (
          <DeviceCell participation={participation} />
        ),
      },
      {
        field: 'effectiveDuration',
        label: t(
          'sessions.details.participantsDetails.columns.effectiveDuration',
        ),
        relevant: isModerated && tabKey === TabStatus.participated,
        sortable: true,
        type: ParticipantFieldType.NUMBER,
        renderCell: (participation) => (
          <DurationCell participation={participation} />
        ),
      },
      ...criteriaFields,
      ...formFields,
      {
        field: 'acceptedAt',
        label: t('sessions.details.participantsDetails.columns.acceptedAt'),
        sortable: true,
        relevant: [
          TabStatus.selected,
          TabStatus.approved,
          TabStatus.gaveUp,
        ].includes(tabKey),
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'startedAt',
        label: t('sessions.details.participantsDetails.columns.startedAt'),
        sortable: true,
        relevant:
          !isModerated &&
          [TabStatus.approved, TabStatus.participated].includes(tabKey),
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'participatedAt',
        label: t('sessions.details.participantsDetails.columns.participatedAt'),
        sortable: true,
        relevant:
          !isModerated &&
          [TabStatus.approved, TabStatus.participated].includes(tabKey),
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'validatedAt',
        label: t('sessions.details.participantsDetails.columns.validatedAt'),
        sortable: true,
        relevant: isModerated
          ? TabStatus.selected === tabKey
          : TabStatus.participated === tabKey,
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'cancelledAt',
        label: isModerated
          ? t('sessions.details.participantsDetails.columns.cancelledAt')
          : t('sessions.details.participantsDetails.columns.gaveUpAt'),
        sortable: true,
        relevant: isModerated
          ? TabStatus.selected === tabKey
          : TabStatus.gaveUp === tabKey,
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'declinedAt',
        label: t('sessions.details.participantsDetails.columns.declinedAt'),
        sortable: true,
        relevant: TabStatus.rejected === tabKey,
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'reportedAt',
        label: t('sessions.details.participantsDetails.columns.reportedAt'),
        sortable: true,
        relevant: isModerated
          ? TabStatus.selected === tabKey
          : TabStatus.approved === tabKey,
        type: ParticipantFieldType.DATE,
      },
      {
        field: 'actions',
        label: t('sessions.details.participantsDetails.columns.actions'),
        relevant: true,
        renderHeader: () => <ActionsHeader />,
        renderCell: (participation) => (
          <ActionsCell
            participation={participation}
            actionsConfig={actionsConfig}
          />
        ),
        renderLoadingCell: () => <ActionsLoader />,
      },
    ],
    [
      t,
      hasTags,
      isModerated,
      tabKey,
      haveDeviceQuota,
      criteriaFields,
      formFields,
      actionsConfig,
    ],
  );
};
